import React, { useRef, useState } from 'react';
import { Button, Card, CardBody } from '@panwds/react-ui';
import { CaretRightIcon, DeleteIcon } from '@panwds/icons';
import { twMerge } from '@panwds/tailwindcss';
import ProductRegionSummary from './ProductRegionSummary';
import PlaneRegionRow from './PlaneRegionRow';
import { CHEVRON_ICON_SIZE, TRASH_ICON_SIZE } from '../utils/iconSizes';
import { BORDERED_CARD_CLASS, CARD_HEADER_HOVER_CLASS } from '../utils/cardStyles';

/**
 * One subscribed product's card in the revamped ManageSubscriptionsModal: a header row
 * (chevron toggle, product name + selected-region summary, remove button) and, when
 * expanded, one `PlaneRegionRow` per enabled plane.
 *
 * The remove affordance is a trash icon rather than an `x` (per GA design review -- a
 * trash can reads as "delete this subscription" where an `x` reads as "dismiss"). It
 * still just calls `onRemoveProduct`; the parent modal owns the confirmation prompt, so
 * this component stays presentational.
 *
 * Uses a plain `Card` + custom header rather than PANWDS `AccordionPanel` because the
 * header needs a second, independent action (the remove button) alongside the
 * expand/collapse toggle -- `AccordionPanel` renders its whole header as a single
 * `role="button"` div, so nesting a real `<button>` inside it would be invalid.
 *
 * The header is a distinct section from the body: it carries the card's header padding, a
 * bottom border when expanded, and owns both the hover highlight and the toggle click for
 * the entire band (including the area around the trash icon). See the inline comment on
 * the header div for why the click lives on the container while `aria-expanded` and the
 * focus ring stay on the inner `<button>`.
 *
 * The region TreeSelect's expand state (which continent/country nodes are open) is kept
 * in a ref local to this component instance, matching the ref-based cache the previous
 * inline implementation used in ManageSubscriptionsModal -- it only needs to survive
 * across this panel's own re-renders, not outlive the panel's lifetime.
 *
 * `onChangeRegions`/`onRemoveRegion` are called with `(planeOrArrayKey, value)` and left
 * to the parent modal to apply -- see `handleProductCheckChange`/`handleRemoveRegion` in
 * ManageSubscriptionsModal, which this preserves unchanged. For single-plane products,
 * changes are keyed by the product's one actual enabled plane (mirroring the `regions`
 * field, matching hydration) while chip removal is keyed by the literal `'regions'` field
 * -- both match the pre-revamp behavior exactly.
 */
const SubscribedProductPanel = ({
    product,
    enabledPlanes,
    checkedEntry,
    isOpen,
    onToggleOpen,
    onRemoveProduct,
    getRegionItems,
    expandedProducts,
    toggleExpandedRegions,
    onChangeRegions,
    onRemoveRegion,
}) => {
    const productType = product.product_type;
    const multiPlane = enabledPlanes.length >= 2;
    const treeExpandStateRef = useRef({});
    const [, forceRerender] = useState(0);

    const getTreeExpanded = (key) => treeExpandStateRef.current[key];
    const setTreeExpanded = (key, expanded) => {
        treeExpandStateRef.current[key] = expanded;
        forceRerender((n) => n + 1);
    };

    const selectedByPlane = enabledPlanes.map(([planeType]) => [
        planeType,
        multiPlane ? (checkedEntry?.[planeType] || []) : (checkedEntry?.regions || []),
    ]);

    // The header band owns the toggle click, so the remove button must stop the event from
    // bubbling into it -- otherwise removing a product would also toggle the panel it sits
    // in, which then re-renders mid-removal.
    const handleRemoveClick = (event) => {
        event.stopPropagation();
        onRemoveProduct();
    };

    return (
        <div className="ssmutw-p-1" data-testid={`manage-product-panel-${productType}`}>
            <Card shadow={false} addClassName={BORDERED_CARD_CLASS}>
                {/*
                  * Header section. The whole band is the hover + click target -- not just the
                  * inner label button -- so the trash icon can sit in the same row while
                  * every other part of the band still toggles the panel.
                  *
                  * Why the click handler is on this div rather than an outer `<button>`:
                  * wrapping the trash `<button>` in another `<button>` is invalid HTML
                  * (nested interactive content), and giving this div `role="button"` would
                  * add a second tab stop that itself contains interactive children. So the
                  * div takes the click as a *convenience enhancement* while the real
                  * keyboard-accessible control stays the `<button>` below, which owns
                  * `aria-expanded` and the focus ring (see accessibility.mdc, which sanctions
                  * exactly this container-click pattern).
                  *
                  * Consequently this container is the single place that calls `onToggleOpen`:
                  * a click on the inner button (mouse, or Enter/Space, which dispatch a
                  * click) bubbles up to here. The inner button must therefore NOT have its
                  * own `onClick`, or clicking it would toggle twice and cancel itself out.
                  * The trash button stops propagation for the same reason.
                  */}
                <div
                    className={twMerge(
                        'ssmutw-flex ssmutw-items-center ssmutw-gap-2 ssmutw-px-4 ssmutw-py-2',
                        // The border is declared unconditionally at width 0, and only its
                        // *width* is toggled by `isOpen` below. Declaring the colour only in
                        // the open branch is what caused a white line to flash on expand:
                        // with no colour set, the closed state computes
                        // `border-color: currentColor`, which is `rgb(255 255 255)` inside the
                        // modal body, so the appearing border animated from white to the card
                        // border colour. Width is not a transitionable colour property, so
                        // toggling it alone is instant and flicker-free.
                        // `border-solid` is mandatory: preflight is disabled app-wide, so a
                        // bare `border-b` paints nothing -- see cardStyles.js.
                        'ssmutw-border-0 ssmutw-border-solid ssmutw-border-ssmu-card-border',
                        CARD_HEADER_HOVER_CLASS,
                        // Border-driven separation from the body below, per PANWDS convention
                        // (headers divide with a border, never a shadow or bg change).
                        // `rounded-b-none` keeps the hover fill flush against that divider
                        // instead of curving away from it.
                        isOpen && 'ssmutw-rounded-b-none ssmutw-border-b',
                    )}
                    onClick={onToggleOpen}
                    data-testid={`manage-product-panel-${productType}-header`}
                >
                    <button
                        type="button"
                        className="ssmutw-flex ssmutw-items-center ssmutw-gap-2 ssmutw-min-w-0 ssmutw-flex-1 ssmutw-bg-transparent ssmutw-border-0 ssmutw-p-0 ssmutw-text-left ssmutw-cursor-pointer"
                        aria-expanded={isOpen}
                        data-testid={`manage-product-panel-${productType}-toggle`}
                    >
                        <CaretRightIcon
                            size={CHEVRON_ICON_SIZE}
                            aria-hidden="true"
                            className={twMerge(
                                'ssmutw-shrink-0 ssmutw-text-ssmu-icon-secondary ssmutw-transition-transform ssmutw-duration-200',
                                isOpen && 'ssmutw-rotate-90',
                            )}
                        />
                        <ProductRegionSummary
                            productType={productType}
                            enabledPlanes={enabledPlanes}
                            selectedByPlane={selectedByPlane}
                            testIdPrefix={`manage-product-${productType}`}
                        />
                    </button>
                    {/*
                      * Red hover is a deliberate stand-in for a "clear-destructive"
                      * appearance PANWDS does not ship: its set is primary /
                      * primary-destructive / secondary / secondary-destructive /
                      * tertiary / tertiary-clear / clear. `secondary-destructive` would
                      * give red hover but also render the trash red *and* bordered at rest,
                      * which the GA mock does not show.
                      *
                      * `[&&]` (a Tailwind arbitrary variant that doubles the class
                      * selector) is load-bearing. PANWDS emits
                      * `.dark\:hover\:twds-text-steel-gray-300:hover:is(.twds-dark *)` and
                      * `index.html` puts `twds-dark` on <html> permanently, so that rule is
                      * always live and outranks a plain `hover:` override. A `dark:`
                      * variant of our own does NOT work either: Tailwind prefixes the
                      * dark-mode class, so `dark:ssmutw-*` compiles to
                      * `:is(.ssmutw-tw-dark *)` -- a class this app never sets -- making
                      * those rules silently dead. Doubling the class wins on specificity
                      * without `!important` and without depending on any ancestor.
                      *
                      * Only the glyph colour is overridden, not the background: the
                      * `--ssmu-*` palette is exposed to Tailwind as bare `var()` strings
                      * with no alpha channel, so a `/10` tinted background compiles to
                      * nothing.
                      */}
                    <Button
                        appearance="tertiary-clear"
                        size="sm"
                        aria-label={`Remove ${productType}`}
                        icon={<DeleteIcon size={TRASH_ICON_SIZE} aria-hidden="true" />}
                        onClick={handleRemoveClick}
                        dataTestId={`manage-product-remove-${productType}`}
                        addClassName="ssmutw-transition-colors [&&]:hover:ssmutw-text-ssmu-icon-error"
                    />
                </div>

                {isOpen && (
                    <CardBody>
                        <div className="ssmutw-ml-0 md:ssmutw-ml-8 ssmutw-flex ssmutw-flex-col ssmutw-gap-4">
                            {multiPlane ? (
                                enabledPlanes.map(([planeType, planeData]) => (
                                    <PlaneRegionRow
                                        key={planeType}
                                        productType={productType}
                                        planeType={planeType}
                                        planeData={planeData}
                                        selectedRegions={checkedEntry?.[planeType]}
                                        items={getRegionItems(productType, planeType)}
                                        treeExpanded={getTreeExpanded(`${productType}-${planeType}`)}
                                        onTreeExpand={(expanded) => setTreeExpanded(`${productType}-${planeType}`, expanded)}
                                        isChipsExpanded={!!expandedProducts[`${productType}-${planeType}`]}
                                        onToggleChipsExpand={() => toggleExpandedRegions(`${productType}-${planeType}`)}
                                        onChangeRegions={(selectedItems) => onChangeRegions(planeType, selectedItems)}
                                        onRemoveRegion={(value) => onRemoveRegion(planeType, value)}
                                        rowTestId={`manage-plane-row-${productType}-${planeType}`}
                                        chipTestIdPrefix={`manage-region-chip-${planeType}`}
                                    />
                                ))
                            ) : (
                                <PlaneRegionRow
                                    productType={productType}
                                    planeType={enabledPlanes[0]?.[0]}
                                    planeData={enabledPlanes[0]?.[1]}
                                    selectedRegions={checkedEntry?.regions}
                                    items={getRegionItems(productType)}
                                    treeExpanded={getTreeExpanded(productType)}
                                    onTreeExpand={(expanded) => setTreeExpanded(productType, expanded)}
                                    isChipsExpanded={!!expandedProducts[productType]}
                                    onToggleChipsExpand={() => toggleExpandedRegions(productType)}
                                    onChangeRegions={(selectedItems) => onChangeRegions(enabledPlanes[0]?.[0], selectedItems)}
                                    onRemoveRegion={(value) => onRemoveRegion('regions', value)}
                                    rowTestId={`manage-plane-row-${productType}-${enabledPlanes[0]?.[0]}`}
                                    chipTestIdPrefix="manage-region-chip-regions"
                                />
                            )}
                        </div>
                    </CardBody>
                )}
            </Card>
        </div>
    );
};

export default React.memo(SubscribedProductPanel);
